import { useCallback, useMemo, useState } from "react";
import { PageMainCard } from "@/core/components/page-components";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import {
  TimeRangePicker,
  type TimeRange,
} from "@/core/components/ui/time-range-picker";
import { HeaderStack } from "@/business-settings/common/business-settings-page";
import { PageHeader } from "@/business-settings/common/page-header";
import { SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useBusiness } from "@/core/business";
import { dayjs } from "@/core/utils/date";
import {
  useGetEnrichedSpiffApprovals,
  type EnrichedSpiffApproval,
} from "@/spiff-approvals";
import { ApprovalsHistoryTable } from "./approvals-history-table";
import { ExportSpiffsCsvModal } from "./export-spiffs-csv-modal";
import { getCompoundId } from "./utils";

const LegacyApprovals = () => {
  const { businessId } = useBusiness();
  const { updateSidebar } = useBusinessSettingsSidebar();

  const [showCsvExportModal, setShowCsvExportModal] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange | null>(null);

  const { data: enrichedApprovals = [], isLoading } =
    useGetEnrichedSpiffApprovals(businessId);

  // Filter to only show legacy approvals (those without pay period dates)
  const legacyApprovals = useMemo(
    () =>
      enrichedApprovals.filter(
        (approval: EnrichedSpiffApproval) =>
          !approval.pay_period_start_date ||
          !approval.pay_period_inclusive_end_date,
      ),
    [enrichedApprovals],
  );

  const filteredApprovals = useMemo(() => {
    if (!timeRange) {
      return legacyApprovals;
    }

    return legacyApprovals.filter((approval: EnrichedSpiffApproval) => {
      const displayDate = dayjs(approval.display_date);
      return (
        displayDate.isSameOrAfter(dayjs(timeRange.from)) &&
        displayDate.isSameOrBefore(dayjs(timeRange.to).endOf("day"))
      );
    });
  }, [legacyApprovals, timeRange]);

  const hasApprovalsInTimeRange = filteredApprovals.length > 0;

  const handleCloseCsvModal = () => {
    setShowCsvExportModal(false);
  };

  const handleShowCsvModal = () => {
    setShowCsvExportModal(true);
  };

  const handleRowClick = useCallback(
    (spiffApprovalId: string, userId: string) => {
      updateSidebar({
        isOpen: true,
        section: SIDEBAR_SECTIONS.spiffApprovalsHistory,
        selectedItemId: getCompoundId(spiffApprovalId, userId),
      });
    },
    [updateSidebar],
  );

  return (
    <Stack gap="sm" className="mx-auto max-w-[2016px]" fullWidth>
      <HeaderStack>
        <PageHeader>Legacy Approvals</PageHeader>
        <Stack direction="row" align="center" gap="lg">
          <TimeRangePicker
            value={timeRange}
            onChange={setTimeRange}
            placeholder="Select date range"
          />
          <Button
            size="sm"
            disabled={!hasApprovalsInTimeRange}
            onClick={handleShowCsvModal}
          >
            Download CSV
          </Button>
        </Stack>
      </HeaderStack>
      <PageMainCard calculatedWidth>
        <ApprovalsHistoryTable
          filteredApprovals={filteredApprovals}
          hasApprovals={legacyApprovals.length > 0}
          isLoading={isLoading}
          onRowClick={handleRowClick}
        />
      </PageMainCard>
      <ExportSpiffsCsvModal
        isOpen={showCsvExportModal}
        onClose={handleCloseCsvModal}
        timeRange={timeRange}
      />
    </Stack>
  );
};

export default LegacyApprovals;
